import { useEffect, useId, useRef, useState } from "react";
import {
  RISK_ANALYTICS_EVENTS,
  RISK_DISCLAIMER_ES,
  RISK_METHODOLOGY_ES,
  trackVehicleRiskEvent,
} from "../../constants/automapVehicleRiskConstants";
import { buildVehicleRiskAnalyticsPayload } from "../../utils/vehicleRiskAnalytics";
import {
  getBuyerVisibleIndicators,
  getVehicleRiskViewModel,
  shouldShowRiskBadge,
} from "../../utils/vehicleRiskDisplay";
import { useOptionalLocale } from "../../intl";
import "../../styles/riskBadge.css";

export default function RiskBadge({
  listing,
  riskLevel,
  size = "medium",
  variant = "default",
  showTooltip = true,
  showLabel = true,
  context = "default",
  hideIfLow = false,
  className = "",
  onBadgeClick,
}) {
  const tooltipId = useId();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const viewedRef = useRef(false);
  const localeContext = useOptionalLocale();

  const subject = listing || (riskLevel ? { riskLevel } : null);
  const vm = getVehicleRiskViewModel(subject, { t: localeContext?.t });
  const riskMoreInfoLabel =
    localeContext?.t?.("automotive.badges.riskMoreInfo") || "Más información sobre señales de revisión";

  useEffect(() => {
    if (!subject || viewedRef.current) return;
    if (hideIfLow && vm.level === "low") return;
    if (variant === "search_card" && !shouldShowRiskBadge(subject, "search_card")) return;
    viewedRef.current = true;
    trackVehicleRiskEvent(
      RISK_ANALYTICS_EVENTS.BADGE_VIEWED,
      buildVehicleRiskAnalyticsPayload(subject, { context, variant, sourceComponent: "risk_badge" })
    );
  }, [subject?.id, vm.level, context, variant, hideIfLow]);

  if (hideIfLow && vm.level === "low") return null;
  if (variant === "search_card" && !shouldShowRiskBadge(subject, "search_card")) return null;

  const labelText = variant === "card" || variant === "search_card" ? vm.shortLabel : vm.displayLabel;
  const visibleIndicators = getBuyerVisibleIndicators(subject);

  const classes = [
    "risk-badge",
    `risk-badge--${size}`,
    `risk-badge--${variant}`,
    `risk-badge--${vm.badgeTone}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes}>
      <span
        className="risk-badge__pill"
        aria-label={vm.displayLabel}
        title={!tooltipOpen ? vm.tooltip : undefined}
        aria-describedby={tooltipOpen ? tooltipId : undefined}
        onClick={onBadgeClick}
        onKeyDown={onBadgeClick ? (e) => e.key === "Enter" && onBadgeClick(e) : undefined}
        role={onBadgeClick ? "button" : undefined}
        tabIndex={onBadgeClick ? 0 : undefined}
      >
        {showLabel ? <span className="risk-badge__label">{labelText}</span> : null}
        {showTooltip ? (
          <button
            type="button"
            className="risk-badge__info"
            aria-label={riskMoreInfoLabel}
            aria-expanded={tooltipOpen}
            onClick={(event) => {
              event.stopPropagation();
              setTooltipOpen((open) => {
                if (!open) {
                  trackVehicleRiskEvent(
                    RISK_ANALYTICS_EVENTS.TOOLTIP_OPENED,
                    buildVehicleRiskAnalyticsPayload(subject, { context, riskLevel: vm.level })
                  );
                }
                return !open;
              });
            }}
          >
            i
          </button>
        ) : null}
      </span>
      {tooltipOpen ? (
        <span id={tooltipId} className="risk-badge__tooltip" role="tooltip">
          <span>{vm.tooltip}</span>
          {visibleIndicators.length && variant !== "search_card" ? (
            <ul className="risk-badge__indicator-list">
              {visibleIndicators.slice(0, 5).map((item) => (
                <li key={item.type || item.message}>{item.message || item.type}</li>
              ))}
            </ul>
          ) : null}
          {variant === "vehicle_detail" || variant === "detail" ? (
            <span className="risk-badge__disclaimer">{RISK_DISCLAIMER_ES}</span>
          ) : null}
          <span className="risk-badge__disclaimer">{RISK_METHODOLOGY_ES}</span>
        </span>
      ) : null}
    </span>
  );
}

export function RiskLevelPill({ riskLevel, className = "" }) {
  const vm = getVehicleRiskViewModel({ riskLevel });
  return (
    <span className={`risk-level-pill risk-level-pill--${vm.badgeTone} ${className}`.trim()}>
      {vm.displayLabel}
    </span>
  );
}
